import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { ControlledShow } from 'framework/show';
import { ProjectSecurityContext, DashboardProject } from 'domain/shared/newProject';
import { useDashboardProjectDetails } from '../dashboard/hooks/useDashboardProjectDetails';
import { omitDeep } from '@apollo/client/utilities/internal';
import { Loading, LoaderSize } from 'framework/ui/mui-builder';

interface DashboardProjectContextData {
  data: DashboardProject;
  refetch: () => Promise<DashboardProject | undefined>;
}

const DashboardProjectDataContext = createContext<Nullable<DashboardProjectContextData>>(null);

export const useDashboardProjectDataContext = () => {
  const context = useContext(DashboardProjectDataContext);
  if (!context) {
    throw new Error('useDashboardProjectDataContext must be used within DashboardProjectDataContext');
  }
  return context;
};

export const DashboardProjectContext = ({
  id,
  children,
}: PropsWithChildren<{
  id: string;
}>) => {
  const { project, refetch } = useDashboardProjectDetails(id);

  const { contextValue, projectPermissions } = useMemo(() => {
    if (!project) {
      return {
        contextValue: null,
        projectAccessPermissions: [],
      };
    }

    return {
      contextValue: { data: omitDeep(project, '__typename'), refetch },
      projectPermissions: project?.accessInfo,
    };
    // TODO: Remove omitDeep after backend update
  }, [project, refetch]);

  if (!project) {
    return <Loading isInitialPosition size={LoaderSize.Small} />;
  }

  return (
    <DashboardProjectDataContext.Provider value={contextValue}>
      <ProjectSecurityContext permissions={projectPermissions}>
        <ControlledShow data={project}>{children}</ControlledShow>
      </ProjectSecurityContext>
    </DashboardProjectDataContext.Provider>
  );
};
